/**
 * useSocket — WebSocket en tiempo real con Socket.io.
 * Se conecta al backend, se autentifica con el JWT y dispara
 * un evento 'choyero_realtime' en window para que useApiStore recargue.
 */
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { getToken, intentarRefreshToken, clearTokens } from '../api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000');

// Deduplicación de notificaciones inmediatas (evita doble toast si poll y socket coinciden)
const _notifInmediatasVistas = new Set();
const _dispatchNotifInmediata = (id, tipo, titulo, mensaje, datos = {}) => {
  if (_notifInmediatasVistas.has(id)) return;
  _notifInmediatasVistas.add(id);
  setTimeout(() => _notifInmediatasVistas.delete(id), 30000); // limpiar después de 30s
  // Evitar crecimiento ilimitado del Set en sesiones muy largas
  if (_notifInmediatasVistas.size > 500) {
    const arr = [..._notifInmediatasVistas];
    _notifInmediatasVistas.clear();
    arr.slice(-250).forEach(vid => _notifInmediatasVistas.add(vid));
  }
  window.dispatchEvent(new CustomEvent('choyero_notif_nueva', {
    detail: {
      notificaciones: [{
        id,
        tipo,
        titulo,
        mensaje,
        datos,
        leida: false,
        creadoEn: new Date().toISOString(),
        _fromSocket: true, // flag para no guardar en vistas de localStorage
      }]
    }
  }));
};

// Eventos que requieren recargar datos según el rol que los recibe
const EVENTOS_RECARGA = [
  'nueva_cotizacion',
  'cotizacion_aceptada',
  'nueva_orden',
  'viaje_status_update',
  'entrega_confirmada',
  'empresa_aprobada',
  'empresa_suspendida',
  'nueva_solicitud',
  'retorno_oferta',
  'retorno_oferta_respuesta',
  'retorno_pagado',
  'nuevo_retorno',
  'nuevo_mensaje',
  'nueva_empresa',
  'nuevo_cliente',
  'solicitud_modificada',
  'notificacion_nueva',
  'nueva_comision',
  'empresa_rechazada',
  'calificacion_pendiente',
];

export const useSocket = (usuario) => {
  const socketRef = useRef(null);

  useEffect(() => {
    const usuarioId = usuario?.id ?? null;
    if (!usuarioId) return;
    const token = getToken();
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;
    // Exponer el socket globalmente para que ChatModal pueda hacer join_viaje/leave_viaje
    window.__choyeroSocket = socket;

    socket.on('connect', () => {
      console.log('[Socket] Conectado como', usuario.nombre);
      window.dispatchEvent(new CustomEvent('choyero_socket_connected'));
    });

    socket.on('connect_error', async (err) => {
      console.warn('[Socket] Error de conexión:', err.message);
      if (err.message === 'Token expirado') {
        socket.disconnect(); // detener reintentos con token expirado
        const refreshed = await intentarRefreshToken();
        if (refreshed) {
          socket.auth.token = getToken();
          socket.connect();
        } else {
          clearTokens();
          window.location.href = '/login';
        }
      }
    });

    // Escuchar todos los eventos de recarga y disparar evento global
    EVENTOS_RECARGA.forEach((evento) => {
      socket.on(evento, (data) => {
        window.dispatchEvent(new CustomEvent('choyero_realtime', { detail: { evento, data } }));

        // Notificaciones inmediatas para nueva_orden (el poll puede llegar tarde para el chofer)
        if (evento === 'nueva_orden' && data?.orden) {
          const o = data.orden;
          _dispatchNotifInmediata(`socket_orden_${o.id}`, 'nueva_orden', '📦 Nueva orden asignada', `${o.origen} → ${o.destino}`, { ordenId: o.id, viajeId: o.viajeId });
          // Evento dedicado para que AppChofer dispare sonido + vibración
          window.dispatchEvent(new CustomEvent('choyero_nueva_orden', { detail: { orden: o } }));
        }
        if (evento === 'empresa_aprobada') {
          _dispatchNotifInmediata('socket_empresa_aprobada', 'empresa_aprobada', '🎉 ¡Tu empresa fue aprobada!', 'Ya puedes empezar a cotizar en Choyero.');
        }
        if (evento === 'empresa_rechazada') {
          _dispatchNotifInmediata('socket_empresa_rechazada', 'empresa_rechazada', '❌ Solicitud rechazada', data?.motivo || 'Tu solicitud fue rechazada. Revisa los documentos.');
        }
        // nuevo_mensaje: despachar evento dedicado con el mensaje completo
        // para que ChatModal lo agregue al state SIN hacer HTTP call
        if (evento === 'nuevo_mensaje' && data?.mensaje) {
          window.dispatchEvent(new CustomEvent('choyero_nuevo_mensaje', { detail: { mensaje: data.mensaje } }));
        }
        // nueva_solicitud, nueva_cotizacion, nueva_empresa, nuevo_cliente se manejan sólo por el poll
        // (choyero_realtime → recargar() → choyero_notif_nueva) para evitar doble toast
      });
    });

    // GPS del chofer — evento independiente (no recarga store)
    socket.on('chofer_posicion', (data) => {
      window.dispatchEvent(new CustomEvent('choyero_gps_update', { detail: data }));
    });

    // Métricas live para el dashboard de admin
    socket.on('admin_metricas_update', () => {
      window.dispatchEvent(new CustomEvent('choyero_admin_metricas'));
    });

    return () => {
      EVENTOS_RECARGA.forEach(evento => socket.off(evento));
      socket.off('chofer_posicion');
      socket.off('admin_metricas_update');
      socket.disconnect();
      socketRef.current = null;
      window.__choyeroSocket = null;
    };
  }, [usuario?.id ?? null]); // eslint-disable-line

  return socketRef;
};

export default useSocket;
